import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

export interface WizardStepConfig {
  label: string;
  description?: string;
  optional?: boolean;
}

export interface WizardContextValue {
  // Current step
  activeStep: number;
  setActiveStep: (step: number) => void;

  // Step configuration
  steps: WizardStepConfig[];
  setSteps: (steps: WizardStepConfig[]) => void;

  // Wizard data (generic object to store any wizard-specific data)
  data: Record<string, any>;
  setData: (key: string, value: any) => void;
  setDataBulk: (data: Record<string, any>) => void;
  getData: <T = any>(key: string, defaultValue?: T) => T;

  // Validation
  stepValidation: Record<number, boolean>;
  setStepValidation: (step: number, isValid: boolean) => void;
  isStepValid: (step: number) => boolean;

  // Navigation
  canGoNext: boolean;
  canGoBack: boolean;
  goNext: () => void;
  goBack: () => void;
  goToStep: (step: number) => void;

  // Completion
  isComplete: boolean;
  complete: () => void;

  // Reset
  reset: () => void;

  // Loading states
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;

  // Error handling
  error: string | null;
  setError: (error: string | null) => void;

  // Persistence
  saveState: () => void;
  loadState: (wizardId: string) => boolean;
  clearSavedState: (wizardId: string) => void;
}

const WizardContext = createContext<WizardContextValue | undefined>(undefined);

export interface WizardProviderProps {
  children: ReactNode;
  initialData?: Record<string, any>;
  persistenceKey?: string; // If provided, wizard state will be persisted to localStorage
}

export const WizardProvider: React.FC<WizardProviderProps> = ({
  children,
  initialData = {},
  persistenceKey
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [steps, setSteps] = useState<WizardStepConfig[]>([]);
  const [data, setDataState] = useState<Record<string, any>>(initialData);
  const [stepValidation, setStepValidationState] = useState<Record<number, boolean>>({});
  const [isComplete, setIsComplete] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setData = useCallback((key: string, value: any) => {
    setDataState(prev => {
      const updated = { ...prev, [key]: value };
      if (persistenceKey) {
        localStorage.setItem(persistenceKey, JSON.stringify({
          activeStep,
          data: updated,
          stepValidation
        }));
      }
      return updated;
    });
  }, [persistenceKey, activeStep, stepValidation]);

  const setDataBulk = useCallback((newData: Record<string, any>) => {
    setDataState(prev => {
      const updated = { ...prev, ...newData };
      if (persistenceKey) {
        localStorage.setItem(persistenceKey, JSON.stringify({
          activeStep,
          data: updated,
          stepValidation
        }));
      }
      return updated;
    });
  }, [persistenceKey, activeStep, stepValidation]);

  const getData = useCallback(<T = any>(key: string, defaultValue?: T): T => {
    return data[key] !== undefined ? data[key] : defaultValue;
  }, [data]);

  const setStepValidation = useCallback((step: number, isValid: boolean) => {
    setStepValidationState(prev => ({ ...prev, [step]: isValid }));
  }, []);

  const isStepValid = useCallback((step: number) => {
    return stepValidation[step] ?? false;
  }, [stepValidation]);

  const canGoNext = activeStep < steps.length - 1 && (stepValidation[activeStep] ?? false);
  const canGoBack = activeStep > 0;

  const goNext = useCallback(() => {
    if (canGoNext) {
      const nextStep = activeStep + 1;
      setActiveStep(nextStep);
      if (persistenceKey) {
        localStorage.setItem(persistenceKey, JSON.stringify({
          activeStep: nextStep,
          data,
          stepValidation
        }));
      }
    }
  }, [activeStep, canGoNext, data, persistenceKey, stepValidation]);

  const goBack = useCallback(() => {
    if (canGoBack) {
      const prevStep = activeStep - 1;
      setActiveStep(prevStep);
      if (persistenceKey) {
        localStorage.setItem(persistenceKey, JSON.stringify({
          activeStep: prevStep,
          data,
          stepValidation
        }));
      }
    }
  }, [activeStep, canGoBack, data, persistenceKey, stepValidation]);

  const goToStep = useCallback((step: number) => {
    if (step >= 0 && step < steps.length) {
      setActiveStep(step);
      if (persistenceKey) {
        localStorage.setItem(persistenceKey, JSON.stringify({
          activeStep: step,
          data,
          stepValidation
        }));
      }
    }
  }, [steps.length, data, persistenceKey, stepValidation]);

  const complete = useCallback(() => {
    setIsComplete(true);
    if (persistenceKey) {
      localStorage.removeItem(persistenceKey);
    }
  }, [persistenceKey]);

  const reset = useCallback(() => {
    setActiveStep(0);
    setDataState(initialData);
    setStepValidationState({});
    setIsComplete(false);
    setIsLoading(false);
    setError(null);
    if (persistenceKey) {
      localStorage.removeItem(persistenceKey);
    }
  }, [initialData, persistenceKey]);

  const saveState = useCallback(() => {
    if (persistenceKey) {
      localStorage.setItem(persistenceKey, JSON.stringify({
        activeStep,
        data,
        stepValidation
      }));
    }
  }, [persistenceKey, activeStep, data, stepValidation]);

  const loadState = useCallback((wizardId: string): boolean => {
    try {
      const saved = localStorage.getItem(wizardId);
      if (saved) {
        const state = JSON.parse(saved);
        setActiveStep(state.activeStep ?? 0);
        setDataState(state.data ?? {});
        setStepValidationState(state.stepValidation ?? {});
        return true;
      }
    } catch (err) {
      console.error('Failed to load wizard state:', err);
    }
    return false;
  }, []);

  const clearSavedState = useCallback((wizardId: string) => {
    localStorage.removeItem(wizardId);
  }, []);

  const value: WizardContextValue = {
    activeStep,
    setActiveStep,
    steps,
    setSteps,
    data,
    setData,
    setDataBulk,
    getData,
    stepValidation,
    setStepValidation,
    isStepValid,
    canGoNext,
    canGoBack,
    goNext,
    goBack,
    goToStep,
    isComplete,
    complete,
    reset,
    isLoading,
    setIsLoading,
    error,
    setError,
    saveState,
    loadState,
    clearSavedState
  };

  return (
    <WizardContext.Provider value={value}>
      {children}
    </WizardContext.Provider>
  );
};

export const useWizard = (): WizardContextValue => {
  const context = useContext(WizardContext);
  if (!context) {
    throw new Error('useWizard must be used within a WizardProvider');
  }
  return context;
};
